import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CreateShipmentMethodRequest, UpdateShipmentMethodRequest } from '@domain/purchasing/IShipmentRepository';
import { container } from '@infrastructure/container';

const SHIPMENT_METHODS_QUERY_KEY = 'shipmentMethods';
const SHIPMENT_METHOD_QUERY_KEY = 'shipmentMethod';

export const useShipmentMethods = () => {
  const queryClient = useQueryClient();

  const { data: shipmentMethods, isLoading, error } = useQuery({
    queryKey: [SHIPMENT_METHODS_QUERY_KEY],
    queryFn: async () => {
      const result = await container.shipmentRepository.getShipmentMethods();
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    }
  });

  const createShipmentMethodMutation = useMutation({
    mutationFn: async (request: CreateShipmentMethodRequest) => {
      const result = await container.shipmentRepository.createShipmentMethod(request);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: () => {
      // Invalidate the shipment methods list
      queryClient.invalidateQueries({ queryKey: [SHIPMENT_METHODS_QUERY_KEY] });
    }
  });

  const updateShipmentMethodMutation = useMutation({
    mutationFn: async ({ id, request }: { id: string; request: UpdateShipmentMethodRequest; }) => {
      const result = await container.shipmentRepository.updateShipmentMethod(id, request);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: (_, { id }) => {
      // Invalidate the shipment methods list
      queryClient.invalidateQueries({ queryKey: [SHIPMENT_METHODS_QUERY_KEY] });
      // Invalidate the specific shipment method query
      queryClient.invalidateQueries({ queryKey: [SHIPMENT_METHOD_QUERY_KEY, id] });
      // Invalidate the products query
      queryClient.invalidateQueries({ queryKey: ['products'] });

    }
  });

  const deleteShipmentMethodMutation = useMutation({
    mutationFn: async (id: string) => {
      const result = await container.shipmentRepository.deleteShipmentMethod(id);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: (_, id) => {
      // Invalidate the shipment methods list
      queryClient.invalidateQueries({ queryKey: [SHIPMENT_METHODS_QUERY_KEY] });
      // Remove the specific shipment method query from cache
      queryClient.removeQueries({ queryKey: [SHIPMENT_METHOD_QUERY_KEY, id] });
    }
  });

  return {
    shipmentMethods,
    isLoading,
    error,
    createShipmentMethod: createShipmentMethodMutation.mutateAsync,
    updateShipmentMethod: updateShipmentMethodMutation.mutateAsync,
    deleteShipmentMethod: deleteShipmentMethodMutation.mutateAsync,
    isCreating: createShipmentMethodMutation.isLoading,
    isUpdating: updateShipmentMethodMutation.isLoading,
    isDeleting: deleteShipmentMethodMutation.isLoading
  };
};

export const useShipmentMethod = (id: string) => {
  const { data: shipmentMethod, isLoading, error } = useQuery({
    queryKey: [SHIPMENT_METHOD_QUERY_KEY, id],
    queryFn: async () => {
      const result = await container.shipmentRepository.getShipmentMethod(id);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    enabled: !!id
  });

  return {
    shipmentMethod,
    isLoading,
    error
  };
};
